import React from 'react';
import { Space, Tooltip } from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import './CompactStatus.css';

export type CompactStatusTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

const SUCCESS_STATUSES = new Set([
  'active', 'approved', 'available', 'clinical_validated', 'completed', 'external_validated',
  'passed', 'platform_qualified', 'published', 'ready', 'success', 'validated',
]);
const DANGER_STATUSES = new Set([
  'error', 'failed', 'invalid', 'package_invalid', 'rejected', 'script_failed', 'source_rejected',
]);
const WARNING_STATUSES = new Set([
  'pending', 'queued', 'review', 'submitted', 'warning', 'waiting',
]);
const INFO_STATUSES = new Set([
  'checking', 'legacy_accepted', 'processing', 'qualification_running', 'running',
]);

export const resolveCompactStatusTone = (status?: string): CompactStatusTone => {
  const normalized = String(status || '').trim().toLowerCase();
  if (SUCCESS_STATUSES.has(normalized)) return 'success';
  if (DANGER_STATUSES.has(normalized)) return 'danger';
  if (WARNING_STATUSES.has(normalized)) return 'warning';
  if (INFO_STATUSES.has(normalized)) return 'info';
  return 'neutral';
};

const TONE_META: Record<CompactStatusTone, { color: string; background: string; border: string; icon: React.ReactNode }> = {
  success: { color: '#397159', background: '#EFF7F2', border: '#CBE2D4', icon: <CheckCircleOutlined /> },
  warning: { color: '#946B24', background: '#FCF7EC', border: '#E9D9B9', icon: <ClockCircleOutlined /> },
  danger: { color: '#984B4B', background: '#FBF1F1', border: '#E8CACA', icon: <CloseCircleOutlined /> },
  info: { color: '#365F86', background: '#F0F5FA', border: '#CDDAE7', icon: <InfoCircleOutlined /> },
  neutral: { color: '#667085', background: '#F6F8FA', border: '#DDE3EA', icon: <MinusCircleOutlined /> },
};

export const CompactStatusGlyph: React.FC<{
  label: string;
  value: string;
  tone: CompactStatusTone;
  hint?: string;
  icon?: React.ReactNode;
}> = ({ label, value, tone, hint, icon }) => {
  const meta = TONE_META[tone];
  const title = (
    <div>
      <strong>{label}：{value}</strong>
      {hint ? <div style={{ marginTop: 3, opacity: 0.82 }}>{hint}</div> : null}
    </div>
  );
  return (
    <Tooltip title={title}>
      <span
        aria-label={`${label}：${value}`}
        style={{
          width: 27,
          height: 27,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: `1px solid ${meta.border}`,
          borderRadius: 7,
          background: meta.background,
          color: meta.color,
          fontSize: 14,
          cursor: 'help',
        }}
      >
        {icon || meta.icon}
      </span>
    </Tooltip>
  );
};

export const CompactStatusLegend: React.FC = () => (
  <Space size={12} wrap aria-label="状态图例">
    {(Object.entries(TONE_META) as Array<[CompactStatusTone, typeof TONE_META[CompactStatusTone]]>).map(([tone, meta]) => {
      const labels: Record<CompactStatusTone, string> = {
        success: '已完成/可用',
        warning: '待处理',
        danger: '异常',
        info: '进行中/存量',
        neutral: '未开始/停用',
      };
      return (
        <span key={tone} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: '#667085', fontSize: 11 }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: meta.color }} />
          {labels[tone]}
        </span>
      );
    })}
  </Space>
);
